import { SUPPORTED_IMAGE_EXTENSIONS, isSupportedImageFile } from './supportedImageFormats'

export type UploadSelection = 'single' | 'multiple' | 'folder'

export interface UploadFilesOptions {
  /** Allowed file extensions (without the dot). Defaults to common image types. */
  allowedTypes?: string[]
  /** How many files / a whole folder the user can pick. */
  selection?: UploadSelection
  /** Called with the filtered list of selected files. */
  onUpload: (files: File[]) => void
}

const DEFAULT_ALLOWED_TYPES: string[] = [...SUPPORTED_IMAGE_EXTENSIONS]

function matchesAllowedTypes(file: File, allowedTypes: string[]): boolean {
  // Default (image) uploads defer to the single source of truth for "supported image"
  // instead of a second, looser predicate (any image/* MIME + extension list).
  if (allowedTypes === DEFAULT_ALLOWED_TYPES) return isSupportedImageFile(file)
  if (file.type.startsWith('image/')) return true
  const extension = file.name.split('.').pop()?.toLowerCase()
  return Boolean(extension && allowedTypes.includes(extension))
}

export function filterAllowedUploadFiles(
  files: File[],
  allowedTypes = DEFAULT_ALLOWED_TYPES,
): File[] {
  return files.filter((file) => matchesAllowedTypes(file, allowedTypes))
}

/**
 * Opens a hidden file input to let the user pick image files (or a whole folder),
 * filters them to the allowed types, and hands the result to `onUpload`.
 *
 * Shared by the Batch entry gate and the SideActions upload button so the
 * selection logic lives in one place.
 */
export function uploadFiles({
  allowedTypes = DEFAULT_ALLOWED_TYPES,
  selection = 'single',
  onUpload,
}: UploadFilesOptions): void {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = allowedTypes.map((type) => `.${type}`).join(',')

  if (selection === 'multiple') {
    input.multiple = true
  } else if (selection === 'folder') {
    input.multiple = true
    // `webkitdirectory` lets the user pick a whole folder; non-standard typing.
    ;(input as unknown as { webkitdirectory: boolean }).webkitdirectory = true
  }

  input.onchange = () => {
    const files = filterAllowedUploadFiles(Array.from(input.files ?? []), allowedTypes)
    if (files.length) onUpload(files)
  }

  input.click()
}

/** Reads a File into a data URL (used to seed the batch slice with image previews). */
export function fileToDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}
